import { faCircleQuestion } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import {
    FormControl,
    FormControlStack,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,
    FormDescription,
} from 'src/registry/ui/Form';
import {
    MultiSelect,
    MultiSelectContent,
    MultiSelectControl,
    MultiSelectOption,
    MultiSelectTrigger,
} from 'src/registry/ui/MultiSelect';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from 'src/registry/ui/Tooltip';

import type { UseFormReturn, FieldValues, FieldPath } from 'react-hook-form';

type MultiSelectFieldProps<TData extends FieldValues> = {
    form: UseFormReturn<TData>;
    name: FieldPath<TData>;
    label: string;
    options?: { id: string; name: string }[];
    required?: boolean;
    variant?: 'dialog' | 'default';
    tooltip?: string;
    description?: string;
    // What the empty control reads, when nothing selected means something other than "nothing"
    placeholder?: string;
};

export default function MultiSelectFormField<TData extends FieldValues>({
    required = true,
    variant = 'dialog',
    name,
    label,
    options,
    placeholder,
    form,
    description,
    tooltip,
}: MultiSelectFieldProps<TData>) {
    if (!options) return null;
    return (
        <FormField
            control={form.control}
            name={name}
            render={({ field }) => {
                const allValues = options?.map((option) => option.id);

                const fieldValue = Array.isArray(field.value) ? field.value : [];
                const fieldData = fieldValue
                    .map((value) => options?.find((option) => option.id === value)?.name)
                    .join(', ');

                const multiSelectField = {
                    ...field,
                    value: fieldValue,
                };

                return (
                    <FormItem variant={variant}>
                        <FormLabel variant={variant} required={required}>
                            {label}
                        </FormLabel>
                        {tooltip && (
                            <TooltipProvider>
                                <Tooltip>
                                    <TooltipTrigger type="button" className="mb-0 ml-1 text-gray-400">
                                        <FontAwesomeIcon icon={faCircleQuestion} />
                                    </TooltipTrigger>
                                    <TooltipContent className="max-w-md">{tooltip}</TooltipContent>
                                </Tooltip>
                            </TooltipProvider>
                        )}
                        <FormControlStack>
                            <MultiSelect {...multiSelectField}>
                                <FormControl>
                                    <MultiSelectTrigger asChild>
                                        <MultiSelectControl>
                                            {fieldData}
                                            {fieldData.length == 0 && (
                                                <span className="text-gray-400">{placeholder ?? `${label}...`}</span>
                                            )}
                                        </MultiSelectControl>
                                    </MultiSelectTrigger>
                                </FormControl>
                                <MultiSelectContent allValues={allValues}>
                                    {options?.map((option) => (
                                        <MultiSelectOption key={option.id} selected={option.id}>
                                            {option.name}
                                        </MultiSelectOption>
                                    ))}
                                </MultiSelectContent>
                            </MultiSelect>
                            {description && <FormDescription>{description}</FormDescription>}
                            <FormMessage />
                        </FormControlStack>
                    </FormItem>
                );
            }}
        />
    );
}
